import nipplejs from "nipplejs";
import * as THREE from "three";

/**
 * Basit mobil / dokunmatik cihaz tespiti.
 * Sadece touch desteği yetmez, "kaba" işaretçi (parmak) olup olmadığını da kontrol ediyoruz
 * ki touch-destekli laptoplar yanlışlıkla mobil moda geçmesin.
 */
export function isMobileDevice() {
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const coarsePointer =
    window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  return hasTouch && coarsePointer;
}

/**
 * TouchControls
 * - Sol alt: nipplejs joystick -> hareket (WASD alternatifi)
 * - Sağ / genel alan: touchmove ile kamera quaternion güncellemesi (fare alternatifi)
 * - Kısa dokunuş (tap): onTap callback'i tetikler (ⓘ ikonlarına tıklama alternatifi)
 */
export class TouchControls {
  constructor(camera, options = {}) {
    this.camera = camera;
    this.enabled = false;

    this.moveState = { x: 0, y: 0, sprint: false };
    this.lookSensitivity = options.lookSensitivity ?? 0.0028;

    this.euler = new THREE.Euler(0, 0, 0, "YXZ");

    this.joystickZone = document.getElementById("joystickZone");
    this.lookZone = document.getElementById("lookZone");
    this.sprintBtn = document.getElementById("sprintBtn");

    this.joystickManager = null;
    this.activeLookTouchId = null;
    this.lastTouch = { x: 0, y: 0 };
    this._tapStart = null;

    // Bir dokunuşun "tap" mi yoksa "sürükleme" mi olduğunu ayırt etmek için eşikler
    this.TAP_MAX_DISTANCE = 12; // px
    this.TAP_MAX_DURATION = 300; // ms

    this.onTap = options.onTap || (() => {});

    this._bindLookEvents();
    this._bindSprintButton();
  }

  _isPopupOpen() {
    const overlay = document.getElementById("welcomeOverlay");
    const infoBox = document.getElementById("infoBox");
    const overlayOpen = overlay && !overlay.classList.contains("wp-hidden");
    const infoBoxOpen = infoBox && !infoBox.classList.contains("hidden");
    return overlayOpen || infoBoxOpen;
  }

  _bindLookEvents() {
    if (!this.lookZone) return;

    this.lookZone.addEventListener("touchstart", this._onLookStart.bind(this), {
      passive: false,
    });
    this.lookZone.addEventListener("touchmove", this._onLookMove.bind(this), {
      passive: false,
    });
    this.lookZone.addEventListener("touchend", this._onLookEnd.bind(this), {
      passive: false,
    });
    this.lookZone.addEventListener("touchcancel", this._onLookEnd.bind(this), {
      passive: false,
    });
  }

  _bindSprintButton() {
    if (!this.sprintBtn) return;

    this.sprintBtn.addEventListener(
      "touchstart",
      (e) => {
        if (this._isPopupOpen()) return; 
        e.preventDefault();
        this.moveState.sprint = true;
        this.sprintBtn.classList.add("active");
      },
      { passive: false }
    );

    this.sprintBtn.addEventListener(
      "touchend",
      (e) => {
        if (this._isPopupOpen()) return; 
        e.preventDefault();
        this.moveState.sprint = false;
        this.sprintBtn.classList.remove("active");
      },
      { passive: false }
    );
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;

    document.body.classList.add("touch-active");

    // DOM'un görünür olmasını garantiye almak için setTimeout kullanıyoruz
    setTimeout(() => {
      if (this.joystickZone && !this.joystickManager) {
        this.joystickManager = nipplejs.create({
          zone: this.joystickZone,
          mode: "static",
          position: { left: "50%", top: "50%" },
          color: "rgba(255, 255, 255, 0.8)",
          size: 120,
          restOpacity: 0.75
        });

        this.joystickManager.on("move", (evt) => {
          const info = evt && evt.data;
          if (!info || !info.vector) return;
          this.moveState.x = info.vector.x;
          this.moveState.y = info.vector.y;
        });

        this.joystickManager.on("end", () => {
          this.moveState.x = 0;
          this.moveState.y = 0;
        });
      }
    }, 100);
  }

  disable() {
    this.enabled = false;
    this.moveState.x = 0;
    this.moveState.y = 0;
    this.moveState.sprint = false;
    document.body.classList.remove("touch-active");
  }

  _onLookStart(e) {
    if (this._isPopupOpen()) return; 
    e.preventDefault();
    const touch = e.changedTouches[0];
    this.activeLookTouchId = touch.identifier;
    this.lastTouch.x = touch.clientX;
    this.lastTouch.y = touch.clientY;
    this._tapStart = {
      x: touch.clientX,
      y: touch.clientY,
      time: performance.now(),
    };
  }

  _onLookMove(e) {
    if (this._isPopupOpen()) return; 
    e.preventDefault();
    if (!this.enabled) return;

    for (const touch of e.changedTouches) {
      if (touch.identifier !== this.activeLookTouchId) continue;

      const movementX = touch.clientX - this.lastTouch.x;
      const movementY = touch.clientY - this.lastTouch.y;

      this.lastTouch.x = touch.clientX;
      this.lastTouch.y = touch.clientY;

      // PointerLockControls'un mouse-look mantığının aynısı
      this.euler.setFromQuaternion(this.camera.quaternion);
      this.euler.y -= movementX * this.lookSensitivity;
      this.euler.x -= movementY * this.lookSensitivity;
      this.euler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.euler.x));
      this.camera.quaternion.setFromEuler(this.euler);
    }
  }

  _onLookEnd(e) {
    if (this._isPopupOpen()) return; 
    e.preventDefault();
    const touch = e.changedTouches[0];
    if (touch.identifier !== this.activeLookTouchId) return;

    if (this._tapStart) {
      const dx = touch.clientX - this._tapStart.x;
      const dy = touch.clientY - this._tapStart.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const duration = performance.now() - this._tapStart.time;

      if (dist < this.TAP_MAX_DISTANCE && duration < this.TAP_MAX_DURATION) {
        this.onTap();
      }
    }

    this.activeLookTouchId = null;
    this._tapStart = null;
  }

  // Her frame'de animate() içinden çağrılır
  getMoveVector() {
    if (!this.enabled) return { forward: 0, right: 0, sprint: false };
    const result =  {
      forward: this.moveState.y,
      right: this.moveState.x,
      sprint: this.moveState.sprint,
    };
    return result;
  }

  destroy() {
    if (this.joystickManager) {
      this.joystickManager.destroy();
      this.joystickManager = null;
    }
  }
}